/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Header.css";
import {
    Avatar, Button, CounterBadge, Menu, MenuDivider, MenuItem, MenuList, MenuPopover,
    MenuTrigger, Popover, PopoverSurface, PopoverTrigger, SearchBox,
} from "@fluentui/react-components";
import {
    VideoSecurityRegular, DiversityFilled,
    SettingsRegular, AlertBadgeRegular, ChatHelpRegular,
    Building20Regular, Search20Regular, Person20Regular, SignOut20Regular,
    Settings20Regular, Checkmark20Regular, QuestionCircle20Regular,
    Keyboard20Regular, Book20Regular, Chat20Regular,
} from '@fluentui/react-icons';
import useAuth from "../../hooks/useAuth";
import { useNotifications } from "./Notificationsstore";
import { scheduleSearch, useSearchStore } from "../Global/Searchstore";
import { groupResults, tokenize } from "../Global/Searchranking";
import { quickLinks } from "../Global/Searchproviders";
import { appMeta } from "../Global/Searchtypes";
import type { SearchAppId, SearchResult, SearchTarget } from "../Global/Searchtypes";
import { Highlight } from "../Global/Searchui";


interface HeaderProps {
    onAppClick: () => void;
    /** Optional. Called with a route path; falls back to a normal page navigation. */
    onNavigate?: (path: string) => void;
    /** Optional. Hyper View button. Falls back to onNavigate("/hyperview"). */
    onHyperView?: () => void;
    /** The app the user is in right now; its results are shown first. */
    currentApp?: SearchAppId | null;
    /** Called when a search result (or quick link) is opened. */
    onOpenResult?: (target: SearchTarget, label: string) => void;
    /** Called to open the full results page for a query. */
    onSeeAll?: (query: string) => void;
    /** Optional. Sign out. Falls back to auth.logout() if your hook has one. */
    onSignOut?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onAppClick, onNavigate, onHyperView, currentApp = null, onOpenResult, onSeeAll, onSignOut }) => {
    const [isActive, setIsActive] = useState(false);
    const query = useSearchStore((st) => st.query);
    const setQuery = useSearchStore((st) => st.setQuery);
    const found = useSearchStore((st) => st.results);
    const running = useSearchStore((st) => st.running);
    const searched = useSearchStore((st) => st.searched);
    const recent = useSearchStore((st) => st.recent);
    const remember = useSearchStore((st) => st.remember);
    const [hi, setHi] = useState(-1);
    const notes = useNotifications((s) => s.notes);
    const markRead = useNotifications((s) => s.markRead);
    const markAllRead = useNotifications((s) => s.markAllRead);
    const [noteOpen, setNoteOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const auth = useAuth();
    const user = auth?.user;

    const go = (path: string) => {
        if (onNavigate) onNavigate(path);
        else window.location.assign(path);
    };

    const unread = notes.filter(n => !n.read).length;

    // Close on outside click (replaces the old blur handler, which killed item clicks)
    useEffect(() => {
        if (!isActive) return;
        const onDown = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) setIsActive(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [isActive]);

    const typed = query.trim();
    const tokens = useMemo(() => tokenize(searched), [searched]);

    const onType = (v: string) => {
        setQuery(v);
        setHi(-1);
        setIsActive(true);
        scheduleSearch(v, { token: user?.token ?? "", currentApp });
    };

    const seeAll = () => {
        if (typed.length < 2) return;
        setIsActive(false);
        remember(typed);
        useSearchStore.getState().run(typed, { token: user?.token ?? "", currentApp });
        if (onSeeAll) onSeeAll(typed);
    };

    const openTarget = (t: SearchTarget, label: string) => {
        setIsActive(false);
        if (typed.length >= 2) remember(typed);
        if (onOpenResult) onOpenResult(t, label);
    };

    interface Row { key: string; label: string; sub?: string; tag?: string; mark?: boolean; run: () => void }
    interface Section { key: string; title: string; rows: Row[] }

    // What the dropdown shows: your app's results first, then the others
    const sections = useMemo<Section[]>(() => {
        if (typed.length < 2) {
            const out: Section[] = [];
            if (recent.length) {
                out.push({
                    key: "recent", title: "Recent searches",
                    rows: recent.slice(0, 5).map(r => ({ key: `r:${r}`, label: r, run: () => onType(r) })),
                });
            }
            const links = quickLinks(currentApp, 5);
            if (links.length) {
                out.push({
                    key: "jump", title: currentApp ? `Jump to in ${appMeta(currentApp).label}` : "Jump to",
                    rows: links.map(l => ({ key: `q:${l.title}`, label: l.title, tag: appMeta(l.app).label, run: () => openTarget(l.target, l.title) })),
                });
            }
            return out;
        }
        const out: Section[] = groupResults(found, currentApp).map(g => ({
            key: g.app,
            title: g.app === currentApp ? `${appMeta(g.app).label} · you are here` : appMeta(g.app).label,
            rows: g.items.slice(0, g.app === currentApp ? 5 : 3).map((r: SearchResult) => ({
                key: r.key, label: r.title, sub: r.subtitle, tag: r.kind, mark: true, run: () => openTarget(r.target, r.title),
            })),
        }));
        out.push({ key: "all", title: "", rows: [{ key: "all", label: `See all results for “${typed}”`, tag: "Enter", run: seeAll }] });
        return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typed, found, currentApp, recent]);

    const flat = useMemo(() => sections.flatMap(sec => sec.rows), [sections]);

    const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setIsActive(true);
            setHi(h => (flat.length ? (h + 1) % flat.length : -1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi(h => (flat.length ? (h <= 0 ? flat.length - 1 : h - 1) : -1));
        } else if (e.key === "Enter") {
            if (!isActive) return;
            e.preventDefault();
            if (hi >= 0 && flat[hi]) flat[hi].run();
            else seeAll();
        } else if (e.key === "Escape") {
            setIsActive(false);
        }
    };

    const signOut = () => {
        if (onSignOut) return onSignOut();
        const a = auth as unknown as { logout?: () => void } | undefined;
        if (a?.logout) a.logout(); else go("/login");
    };

    const fullName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "User";

    const footLinks: { label: string; path: string }[] = [
        { label: "Help", path: "/help" },
        { label: "Contact us", path: "/contact" },
        { label: "Cilpron Account", path: "/account" },
    ];

    return (
        <div>
            <div className="OHPMTitleBar">
                <div className="M7RMk624fi5g/EJCqDnwxQ== removeFocusOutline">
                    <div className="dQ8XLMIyNxl8MAYqMsUZFQ== ohpmscs-base ohpmssx-navbar ohpmssx-search">
                        <div id="owhpms-header-leaftRegion" className="ieRo/OqPYB7kffRFKx2c9w==">
                            <div className="Bh46b1rd8BrU4zolL9RzUg==">
                                <Button size="small" aria-label="Apps" title="Apps" onClick={onAppClick} icon={<DiversityFilled className="icpn-3276g" />} className="clgiLVKPzugZZns0LiPTqw== owhpmsx-waffle owhpmssx-button"></Button>
                            </div>
                        </div>
                        <div id="center-region" className="_25CqRVKwE38X5RHExD4IyA== UWEN_TG72B">
                            <div className="divison">
                                <div>
                                    <div className="position-relative">
                                        <div>
                                            <div className="_1QSK5lUhw5Gkh7SDz97ZPQ== __REN1PQ== ">
                                                <div id="search-region" className="_1QSK5lUhw5Gkh7SDz97ZPQ== YENEIH3-==">
                                                    <div className="_1QSK5lUhw5Gkh7SDz97ZPQ== _NDKWIWBDEEO=">
                                                        <div id="tenant-logo" className="M3pcB5evSAtYMozck1WU7A== _3NEUWG3==">

                                                        </div>
                                                        <div id="YEB_29828J" className="M3pcB5evSAtYMozck1WU7A== _Y3==">
                                                            <div className="e/Vl0j0zHQMhZT1vQ6J+SQ== owhpmssx-appName">
                                                                <a href="/" className="AzzTpbJF15SioND4vylW8A== owhpmssx-appBrandLink">
                                                                    <span className="hpyHhmSe9hmk5gopLr9a5Q==">CILPRON</span>
                                                                </a>
                                                            </div>
                                                        </div>
                                                        <div className="M3pcB5evSAtYMozck1WU7A== _UY268N==">
                                                            <div className="siUMOJwnumycxvszBe3uzQ==">
                                                                <div className="siUMOJwnumycxvszBe3uzQ=="></div>
                                                            </div>
                                                        </div>

                                                    </div>
                                                    <div className="M3pcB5evSAtYMozck1WU7A== YSERCH==">
                                                        <div className="siUMOJwnumycxvszBe3uzQ==">
                                                            <div className="siUMOJwnumycxvszBe3uzQ==">
                                                                <div className="x8k1J b_3A1 _ue63h">
                                                                    <div ref={searchRef} className={`UXl34 search-bar-container leftNavOn ${isActive ? "dropdownOn" : ""} `}>
                                                                        <div className="Q_UnC b_3A1">
                                                                            <SearchBox className={`${isActive ? "BE326_==" : ""} `}
                                                                                appearance="filled-darker"
                                                                                placeholder="Search all apps..."
                                                                                value={query}
                                                                                onChange={(_, d) => onType(d.value)}
                                                                                onFocus={() => setIsActive(true)}
                                                                                onClick={() => setIsActive(true)}
                                                                                onKeyDown={onSearchKey}
                                                                                role="combobox"
                                                                                aria-expanded={isActive}
                                                                                aria-controls="hd-search-list"
                                                                                autoComplete="off"
                                                                            />
                                                                        </div>
                                                                        {isActive && (
                                                                            <div className="light-mode">
                                                                                <div className="search-dropdown-container hd-dd">
                                                                                    <div className="search-dropdown-list hd-dd-list">
                                                                                        <div className="search-dropdown-recommendation">
                                                                                            <div className="search-dropdown-recommendation-group" id="hd-search-list" role="listbox">
                                                                                                {sections.length === 0 && (
                                                                    <div className="hd-empty">
                                                                        <Search20Regular />
                                                                        <span>Type two letters to search every app</span>
                                                                    </div>
                                                                )}
                                                                {typed.length >= 2 && running && found.length === 0 && (
                                                                    <div className="hd-empty">
                                                                        <Search20Regular />
                                                                        <span>Searching…</span>
                                                                    </div>
                                                                )}
                                                                {sections.map(sec => (
                                                                    <React.Fragment key={sec.key}>
                                                                        {sec.title && <h5 className="search-dropdown-recommendation-group-title">{sec.title}</h5>}
                                                                        {sec.rows.map(row => {
                                                                            const i = flat.indexOf(row);
                                                                            return (
                                                                                <div
                                                                                    key={row.key}
                                                                                    className={`search-dropdown-recommendation-item hd-item${i === hi ? " hd-item-on" : ""}`}
                                                                                    role="option"
                                                                                    aria-selected={i === hi}
                                                                                    tabIndex={-1}
                                                                                    onMouseEnter={() => setHi(i)}
                                                                                    onClick={row.run}
                                                                                >
                                                                                    <div className="search-dropdown-recommendation-item-text-container">
                                                                                        <Search20Regular className="hd-sico" />
                                                                                        <div className="search-dropdown-recommendation-item-text-title-container">
                                                                                            <div className="search-dropdown-recommendation-item-text-title">
                                                                                                {row.mark ? <Highlight text={row.label} tokens={tokens} /> : <span>{row.label}</span>}
                                                                                                {row.sub ? <span> — {row.sub}</span> : null}
                                                                                            </div>
                                                                                        </div>
                                                                                        {row.tag && <span className="hd-app">{row.tag}</span>}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </React.Fragment>
                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="cp-ai-search-footer hd-foot">
                                                                                        <div className="search-dropdown-quick-links">
                                                                                            <div className="search-dropdown-quick-links-group-container">
                                                                                                <div className="search-dropdown-quick-links-group">
                                                                                                    {footLinks.map((f, i) => (
                                                                                                        <div className="search-dropdown-quick-links-item" key={f.label} id={`search-dropdown-quick-links-link-item-${i}`}>
                                                                                                            <div className="search-dropdown-quick-links-item-link hd-link" role="link" tabIndex={0}
                                                                                                                onClick={() => { setIsActive(false); go(f.path); }}
                                                                                                                onKeyDown={e => { if (e.key === "Enter") { setIsActive(false); go(f.path); } }}>
                                                                                                                <span>{f.label}</span>
                                                                                                            </div>
                                                                                                            {i < footLinks.length - 1 && <span> &nbsp;&nbsp;| </span>}
                                                                                                        </div>
                                                                                                    ))}
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div id="btn-region1" className="_1QSK5lUhw5Gkh7SDz97ZPQ== RTH==">
                                                    <div className="M3pcB5evSAtYMozck1WU7A==">
                                                        <div className="siUMOJwnumycxvszBe3uzQ==">
                                                            <div className="siUMOJwnumycxvszBe3uzQ==">
                                                                <div>
                                                                    <div className="Wpyud R3CoF owhmsx-button">
                                                                        <Button size="small" icon={<VideoSecurityRegular />}
                                                                            onClick={() => (onHyperView ? onHyperView() : go("/hyperview"))}>
                                                                            <span className="cpn-Button-label label-169" id="id__326">Hyper View</span>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div id="btn-region2" className="_1QSK5lUhw5Gkh7SDz97ZPQ== RTH==">
                                                    <div className="M3pcB5evSAtYMozck1WU7A==">
                                                        <div className="siUMOJwnumycxvszBe3uzQ==">
                                                            <div className="siUMOJwnumycxvszBe3uzQ==">
                                                                <div>
                                                                    <div className="Wpyud R3CoF owhmsx-button hd-bell">
                                                                        <Popover open={noteOpen} onOpenChange={(_, d) => setNoteOpen(d.open)} positioning="below-end">
                                                                            <PopoverTrigger disableButtonEnhancement>
                                                                                <Button size="small" icon={<AlertBadgeRegular />} aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`} title="Notifications" />
                                                                            </PopoverTrigger>
                                                                            <PopoverSurface className="hd-pop">
                                                                                <div className="hd-pop-head">
                                                                                    <strong>Notifications</strong>
                                                                                    <Button size="small" appearance="transparent" icon={<Checkmark20Regular />} disabled={!unread}
                                                                                        onClick={markAllRead}>Mark all read</Button>
                                                                                </div>
                                                                                <div className="hd-pop-list">
                                                                                    {notes.length === 0 && <div className="hd-empty"><span>You&apos;re all caught up</span></div>}
                                                                                    {notes.map(n => (
                                                                                        <button key={n.id} type="button" className={`hd-note${n.read ? "" : " hd-note-new"}`}
                                                                                            onClick={() => markRead(n.id)}>
                                                                                            <span className="hd-note-dot" />
                                                                                            <span className="hd-note-txt">
                                                                                                <span className="hd-note-title">{n.title}</span>
                                                                                                <span className="hd-note-body">{n.body}</span>
                                                                                                <span className="hd-note-time">{n.time}</span>
                                                                                            </span>
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                                <div className="hd-pop-foot">
                                                                                    <Button size="small" appearance="transparent" onClick={() => { setNoteOpen(false); go("/notifications"); }}>View all</Button>
                                                                                </div>
                                                                            </PopoverSurface>
                                                                        </Popover>
                                                                        {unread > 0 && <CounterBadge className="hd-badge" count={unread} size="extra-small" color="danger" />}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div id="btn-region3" className="_1QSK5lUhw5Gkh7SDz97ZPQ== RTH==">
                                                    <div className="M3pcB5evSAtYMozck1WU7A==">
                                                        <div className="siUMOJwnumycxvszBe3uzQ==">
                                                            <div className="siUMOJwnumycxvszBe3uzQ==">
                                                                <div>
                                                                    <div className="Wpyud R3CoF owhmsx-button">
                                                                        <Menu positioning="below-end">
                                                                            <MenuTrigger disableButtonEnhancement>
                                                                                <Button size="small" icon={<ChatHelpRegular />} aria-label="Help" title="Help" />
                                                                            </MenuTrigger>
                                                                            <MenuPopover>
                                                                                <MenuList>
                                                                                    <MenuItem icon={<QuestionCircle20Regular />} onClick={() => go("/help")}>Help center</MenuItem>
                                                                                    <MenuItem icon={<Book20Regular />} onClick={() => go("/help/guides")}>User guides</MenuItem>
                                                                                    <MenuItem icon={<Keyboard20Regular />} onClick={() => go("/help/shortcuts")}>Keyboard shortcuts</MenuItem>
                                                                                    <MenuDivider />
                                                                                    <MenuItem icon={<Chat20Regular />} onClick={() => go("/contact")}>Contact support</MenuItem>
                                                                                </MenuList>
                                                                            </MenuPopover>
                                                                        </Menu>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div id="btn-region4" className="_1QSK5lUhw5Gkh7SDz97ZPQ== RTH==">
                                                    <div className="M3pcB5evSAtYMozck1WU7A==">
                                                        <div className="siUMOJwnumycxvszBe3uzQ==">
                                                            <div className="siUMOJwnumycxvszBe3uzQ==">
                                                                <div>
                                                                    <div className="Wpyud R3CoF owhmsx-button">
                                                                        <Menu positioning="below-end">
                                                                            <MenuTrigger disableButtonEnhancement>
                                                                                <Button size="small" icon={<SettingsRegular />} aria-label="Settings" title="Settings" />
                                                                            </MenuTrigger>
                                                                            <MenuPopover>
                                                                                <MenuList>
                                                                                    <MenuItem icon={<Settings20Regular />} onClick={() => go("/settings")}>All settings</MenuItem>
                                                                                    <MenuItem icon={<Person20Regular />} onClick={() => go("/settings/profile")}>My profile</MenuItem>
                                                                                    <MenuItem icon={<Building20Regular />} onClick={() => go("/settings/company")}>Company</MenuItem>
                                                                                </MenuList>
                                                                            </MenuPopover>
                                                                        </Menu>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="M3pcB5evSAtYMozck1WU7A== FdC5CgNe2qQBXGbOKlNuew==">
                                                    <div></div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="HV6lC9J8OUFpKJjfAr1Asw== owhmsx-button">
                            <div className="Bh46b1rd8BrU4zolL9RzUg_U1==">
                                <Menu positioning="below-end">
                                    <MenuTrigger disableButtonEnhancement>
                                        <button type="button" className="hd-avatar-btn" aria-label="Account menu" title={fullName}>
                                            <Avatar name={fullName}
                                                active="inactive"
                                                badge={{ status: "available" }} />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <div className="hd-who">
                                                <strong>{fullName}</strong>
                                                <span>{user?.email ?? ""}</span>
                                            </div>
                                            <MenuDivider />
                                            <MenuItem icon={<Person20Regular />} onClick={() => go("/settings/profile")}>My account</MenuItem>
                                            <MenuItem icon={<Settings20Regular />} onClick={() => go("/settings")}>Settings</MenuItem>
                                            <MenuDivider />
                                            <MenuItem icon={<SignOut20Regular />} onClick={signOut}>Sign out</MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Header;